import React, { useState, useCallback, useMemo } from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { ADD_ATTRIBUTE, PRODUCT_SORTING } from 'Pages/GMS/ISR/ISRConstants';
import { AddIcon } from 'assests/GmsIcons';
import { ProductSortingTable } from './ProductSortingTable';
import {
    TProductCategory,
    TSecondarySortOption,
    TProductSortingItem,
    SortType,
} from '../model';
import { ConfirmationDialog } from 'Pages/GMS/ISR/shared/components/Common/ConfirmationDialog';
import Button from 'Components/DesignSystem/Button/Button';
import Card from 'Components/DesignSystem/Card/Card';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { AttributeSelectionDialog } from './AttributeSelectionDialog';

const COMPONENT_ID = 'product-sorting';

type TAttributeOptionWithDatatype = {
    id: string;
    name: string;
    datatype: 'TEXT' | 'INTEGER' | 'DECIMAL';
};

const generateTooltipMessage = (
    attributeName: string,
    sortType: SortType,
    isAscending: boolean
): string => {
    if (sortType === 'SYSTEM') {
        if (attributeName.toLowerCase() === 'recommended') {
            return 'Sorting by Compatio generated Score';
        }
        return `Sorting by ${attributeName.toLowerCase()}`;
    }

    if (sortType === 'TEXT') {
        return isAscending
            ? `Sorting by ${attributeName} in the order (A to Z)\nDefault Title: ${attributeName} (A to Z)`
            : `Sorting by ${attributeName} in the order (Z to A)\nDefault Title: ${attributeName} (Z to A)`;
    }

    return isAscending
        ? `Sorting by ${attributeName} in the order (Low to High)\nDefault Title: ${attributeName} (Low to High)`
        : `Sorting by ${attributeName} in the order (High to Low)\nDefault Title: ${attributeName} (High to Low)`;
};

type ProductSortingProps = {
    showAddAttributeMenu: boolean;
    setShowAddAttributeMenu: React.Dispatch<React.SetStateAction<boolean>>;
    selectedCategory: TProductCategory;
    updateFieldDataConfiguration: (updatedConfig: TProductCategory) => void;
};

const ProductSorting = ({
    showAddAttributeMenu,
    setShowAddAttributeMenu,
    selectedCategory,
    updateFieldDataConfiguration,
}: ProductSortingProps) => {
    const [isSaving, setIsSaving] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState<{
        isOpen: boolean;
        sortId: string;
        sortTitle: string;
    }>({ isOpen: false, sortId: '', sortTitle: '' });

    const sortOptions = useMemo(() => {
        return (selectedCategory?.data?.productSorting as TProductSortingItem[]) ?? [];
    }, [selectedCategory?.data?.productSorting]);

    const categoryId = useMemo(() => {
        const categories = selectedCategory?.data?.categories?.data ?? [];
        return categories.length > 0 ? categories[0]?.id : '';
    }, [selectedCategory?.data?.categories?.data]);

    const secondarySortOptions = useMemo((): TSecondarySortOption[] => {
        const baseOption: TSecondarySortOption = { id: '', label: 'None' };
        const dynamicOptions: TSecondarySortOption[] = sortOptions.map((option) => ({
            id: option.primary_sort_id,
            label: option.primary_sort_title,
        }));
        return [baseOption, ...dynamicOptions];
    }, [sortOptions]);

    const openAddAttributeMenu = () => setShowAddAttributeMenu(true);
    const closeAddAttributeMenu = () => setShowAddAttributeMenu(false);

    const updateProductSorting = useCallback(
        (updatedOptions: TProductSortingItem[]) => {
            const updatedCategory: TProductCategory = {
                ...selectedCategory,
                data: {
                    ...selectedCategory.data,
                    productSorting: updatedOptions,
                },
            };
            updateFieldDataConfiguration(updatedCategory);
        },
        [selectedCategory, updateFieldDataConfiguration]
    );

    const handleSaveAttribute = async (selectedAttribute: TAttributeOptionWithDatatype | null) => {
        if (!selectedAttribute) return;

        setIsSaving(true);

        try {
            const currentMaxOrder = sortOptions.length > 0
                ? Math.max(...sortOptions.map((opt) => opt.sort_order))
                : 0;

            const attributeType: SortType = selectedAttribute.datatype;
            const isText = attributeType === 'TEXT';

            const ascendingTitle = isText
                ? `${selectedAttribute.name} (A to Z)`
                : `${selectedAttribute.name} (Low to High)`;
            const descendingTitle = isText
                ? `${selectedAttribute.name} (Z to A)`
                : `${selectedAttribute.name} (High to Low)`;

            const newSortOptions: TProductSortingItem[] = [
                {
                    is_visible: true,
                    primary_sort_id: selectedAttribute.id,
                    primary_sort_title: ascendingTitle,
                    secondary_sort_id: 'recommended',
                    secondary_sort_title: 'Recommended',
                    sort_order: currentMaxOrder + 1,
                    sort_type: attributeType,
                    tool_tip_message: generateTooltipMessage(selectedAttribute.name, attributeType, true),
                },
                {
                    is_visible: true,
                    primary_sort_id: selectedAttribute.id,
                    primary_sort_title: descendingTitle,
                    secondary_sort_id: 'recommended',
                    secondary_sort_title: 'Recommended',
                    sort_order: currentMaxOrder + 2,
                    sort_type: attributeType,
                    tool_tip_message: generateTooltipMessage(selectedAttribute.name, attributeType, false),
                },
            ];

            const updatedOptions = [...sortOptions, ...newSortOptions];
            updateProductSorting(updatedOptions);
            closeAddAttributeMenu();
            toast.success('Sort options added successfully');
        } catch (error) {
            console.error('Failed to add attribute:', error);
            toast.error('Failed to add sort options');
        } finally {
            setIsSaving(false);
        }
    };

    const handleReorder = useCallback(
        (reorderedOptions: TProductSortingItem[]) => {
            updateProductSorting(reorderedOptions);
        },
        [updateProductSorting]
    );

    const handleVisibilityChange = useCallback(
        (primarySortId: string, sortOrder: number, isVisible: boolean) => {
            const enabledCount = sortOptions.filter((opt) => opt.is_visible).length;

            if (!isVisible && enabledCount <= 1) {
                toast.error('At least one sort option must be enabled');
                return;
            }

            const updatedOptions = sortOptions.map((option) =>
                option.primary_sort_id === primarySortId && option.sort_order === sortOrder
                    ? { ...option, is_visible: isVisible }
                    : option
            );
            updateProductSorting(updatedOptions);
        },
        [sortOptions, updateProductSorting]
    );

    const handleSecondarySortChange = useCallback(
        (primarySortId: string, sortOrder: number, secondarySortId: string, secondarySortTitle: string) => {
            const updatedOptions = sortOptions.map((option) =>
                option.primary_sort_id === primarySortId && option.sort_order === sortOrder
                    ? {
                        ...option,
                        secondary_sort_id: secondarySortId,
                        secondary_sort_title: secondarySortTitle,
                    }
                    : option
            );
            updateProductSorting(updatedOptions);
        },
        [sortOptions, updateProductSorting]
    );

    const handleTitleChange = useCallback(
        (primarySortId: string, sortOrder: number, title: string) => {
            const updatedOptions = sortOptions.map((option) =>
                option.primary_sort_id === primarySortId && option.sort_order === sortOrder
                    ? { ...option, primary_sort_title: title }
                    : option
            );
            updateProductSorting(updatedOptions);
        },
        [sortOptions, updateProductSorting]
    );

    const handleDeleteClick = useCallback((primarySortId: string) => {
        const option = sortOptions.find((opt) => opt.primary_sort_id === primarySortId);
        if (option) {
            const attributeName = option.primary_sort_title.replace(/\s*\((A to Z|Z to A|Low to High|High to Low)\)$/, '');
            setDeleteConfirmation({
                isOpen: true,
                sortId: primarySortId,
                sortTitle: attributeName,
            });
        }
    }, [sortOptions]);

    const handleDeleteConfirm = useCallback(() => {
        const { sortId } = deleteConfirmation;
        const updatedOptions = sortOptions
            .filter((option) => option.primary_sort_id !== sortId)
            .map((option, idx) => ({
                ...option,
                sort_order: idx + 1,
            }));

        updateProductSorting(updatedOptions);
        setDeleteConfirmation({ isOpen: false, sortId: '', sortTitle: '' });
        toast.success('Sort options deleted successfully');
    }, [deleteConfirmation, sortOptions, updateProductSorting]);

    const handleDeleteCancel = useCallback(() => {
        setDeleteConfirmation({ isOpen: false, sortId: '', sortTitle: '' });
    }, []);

    const existingAttributeIds = useMemo(() => {
        return sortOptions
            .filter((opt) => opt.sort_type === 'TEXT' || opt.sort_type === 'INTEGER' || opt.sort_type === 'DECIMAL')
            .map((opt) => opt.primary_sort_id)
            .filter((value, index, self) => self.indexOf(value) === index);
    }, [sortOptions]);

    return (
        <Card id={COMPONENT_ID} data-testid={COMPONENT_ID}>
            <div
                id={`${COMPONENT_ID}-header`}
                data-testid={`${COMPONENT_ID}-header`}
                className="tw-flex tw-justify-between tw-items-center tw-mb-4"
            >
                <Typography
                    variant="title-header"
                    as="h3"
                    id={`${COMPONENT_ID}-title`}
                    data-testid={`${COMPONENT_ID}-title`}
                    className="tw-text-ink"
                >
                    {PRODUCT_SORTING}
                </Typography>
                {/* `btn-outlined` was layered over a DS `tertiary` Button — two
                    conflicting sets of padding, height and border on one element. The
                    variant already supplies all of it. */}
                <Button variant="tertiary" icon={<AddIcon color="currentColor" />} iconSize="small" showIcon
                    data-testid={`${COMPONENT_ID}-add-attribute-btn`}
                    type="button"
                    onClick={openAddAttributeMenu}
                    disabled={categoryId.length === 0}>
                    {ADD_ATTRIBUTE}
                </Button>
            </div>

            <ProductSortingTable
                sortOptions={sortOptions}
                onReorder={handleReorder}
                onVisibilityChange={handleVisibilityChange}
                onSecondarySortChange={handleSecondarySortChange}
                onTitleChange={handleTitleChange}
                onDelete={handleDeleteClick}
                secondarySortOptions={secondarySortOptions}
            />

            <AttributeSelectionDialog
                isOpen={showAddAttributeMenu}
                onClose={closeAddAttributeMenu}
                title={PRODUCT_SORTING}
                categoryId={categoryId}
                onSave={handleSaveAttribute}
                saveButtonText="Add Attribute"
                cancelButtonText="Cancel"
                isLoading={isSaving}
                existingAttributeIds={existingAttributeIds}
            />

            <ConfirmationDialog
                id="delete-sort-option-dialog"
                isOpen={deleteConfirmation.isOpen}
                title="Delete Sort Option"
                message="Are you sure you want to delete this sort option?"
                confirmText="Delete"
                cancelText="Cancel"
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
            />
        </Card>
    );
};

export default ProductSorting;